import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useMemo } from 'react'
import { comparePeriods } from '../utils/getOrderedPeriods'
import useOrganizationCurrency from './organizationCurrency'
import useOrganizationSettings from './useOrganizationSettings'

interface UseProviderSpendInformationOptions {
  enabled?: boolean
  includeProvidersSpendPeriodsFacets?: boolean
}

export default function useProviderSpendInformation({
  enabled = true,
  includeProvidersSpendPeriodsFacets = true,
}: UseProviderSpendInformationOptions = {}) {
  const { settings: organizationSettings, isLoading: isOrganizationSettingsLoading } = useOrganizationSettings({
    enabled,
  })
  const { organizationCurrency, isLoading: isCurrencyLoading } = useOrganizationCurrency({
    enabled: enabled && !isOrganizationSettingsLoading,
    currencyUnitId: organizationSettings?.currencyUnitId,
  })
  const organizationSpendPeriodFocus = organizationSettings?.spendPeriodFocus
  const hasOrganizationSpendPeriodFocus = Boolean(organizationSpendPeriodFocus)
  const shouldFetchProvidersSpendPeriodsFacets =
    enabled &&
    (includeProvidersSpendPeriodsFacets || (!isOrganizationSettingsLoading && !hasOrganizationSpendPeriodFocus))

  const { facets } = useFetchFacets({
    key: FetchKey.ProviderFacets,
    endpoint: endpoints.providersWithFacets,
    facetsParam: [{ name: 'spends.periodName' }],
    options: {
      enabled: shouldFetchProvidersSpendPeriodsFacets,
      staleTime: ONE_MINUTE_MS,
    },
  })

  // facets is FacetItem[][], we want the first (and only) facet array
  const providersSpendPeriods = facets?.[0] || []

  const spendPeriodFocus = useMemo(() => {
    if (organizationSpendPeriodFocus) {
      return organizationSpendPeriodFocus
    }

    return providersSpendPeriods
      ?.map((facet: { label: string }) => facet.label)
      .sort(comparePeriods)
      .at(-1)
  }, [organizationSpendPeriodFocus, providersSpendPeriods, comparePeriods])

  return {
    organizationCurrency,
    isCurrencyLoading: isCurrencyLoading || isOrganizationSettingsLoading,
    providersSpendPeriods,
    spendPeriodFocus,
  }
}
